import React, { useRef, useState } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { streamAsk } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";

const PRESET = [
  "這家公司目前最大的機會與風險是什麼？",
  "以巴菲特角度，這家公司是否值得長線持有？",
  "與同業相比，估值是否合理？",
];

export default function AIChat({ ticker, name }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ansRef = useRef(null);

  const ask = async (question) => {
    if (!question.trim() || loading) return;
    setAnswer("");
    setError("");
    setLoading(true);
    await streamAsk(
      ticker,
      question,
      (delta) => {
        setAnswer((prev) => prev + delta);
        if (ansRef.current) ansRef.current.scrollTop = ansRef.current.scrollHeight;
      },
      () => setLoading(false),
      (err) => {
        setError(err || "AI 服務暫時無法回應");
        setLoading(false);
      }
    );
  };

  return (
    <section>
      <SectionTitle icon={Sparkles} title="AI 對話研究員" subtitle={`Ask AI · ${name}`} />

      <div className="card-tactical p-5 sm:p-6">
        <div className="text-xs text-zinc-500 mb-3">
          使用 Claude Sonnet 4.5，針對 <span className="font-mono text-zinc-300">{ticker}</span> 的
          即時提問。回答為研究參考，非投資建議。
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {PRESET.map((p) => (
            <button
              key={p}
              onClick={() => {
                setQ(p);
                ask(p);
              }}
              className="text-xs px-2.5 py-1.5 rounded-md border border-zinc-800 bg-zinc-950 hover:border-zinc-600 text-zinc-300 transition-colors"
            >
              {p}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            data-testid={TID.aiChatInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask(q)}
            placeholder="輸入你想問的問題..."
            className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none"
          />
          <button
            data-testid={TID.aiChatSend}
            onClick={() => ask(q)}
            disabled={loading || !q.trim()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            {loading ? "生成中" : "送出"}
          </button>
        </div>

        {(answer || loading || error) && (
          <div
            ref={ansRef}
            data-testid={TID.aiChatResponse}
            className="mt-4 max-h-96 overflow-auto rounded-md border border-zinc-800 bg-zinc-950 p-4 text-sm leading-relaxed whitespace-pre-wrap text-zinc-200"
          >
            {error ? (
              <span className="text-red-400">{error}</span>
            ) : (
              <>
                {answer}
                {loading && <span className="inline-block w-2 h-4 bg-blue-400 ml-1 align-middle blink" />}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
